"use client";

import type { ReactNode } from "react";
import { Check } from "../lib/icons";

export type MetricReviewStatus =
  | "needs-review"
  | "approved"
  | "change-requested"
  | "revising"
  | "updated";

export interface MetricReviewCardProps {
  /** Stable identifier for the metric heading and outer article. */
  id: string;
  name: string;
  definition?: ReactNode;
  unit?: ReactNode;
  /** Rendered formula content, such as `<FormulaExpression expression={formula} />`. */
  formula?: ReactNode;
  status: MetricReviewStatus;
  /** Controlled state for the inline change-request editor. */
  isChangeRequestEditorOpen?: boolean;
  changeRequestDraft?: string;
  /** Saved change request displayed after it is committed. */
  changeRequest?: ReactNode;
  /** Shows a presentation-only processing overlay while a revision is generated. */
  isProcessing?: boolean;
  /** Disables all review controls while the graph processes a decision. */
  disabled?: boolean;

  onApprove?: () => void;
  onRequestChange?: () => void;
  onChangeRequestDraft?: (value: string) => void;
  onSaveChangeRequest?: (value: string) => void;
  onCancelChangeRequest?: () => void;
  onEditChangeRequest?: () => void;
  onRemoveChangeRequest?: () => void;
}

const STATUS: Record<MetricReviewStatus, { label: string; className: string }> =
  {
    "needs-review": {
      label: "Needs review",
      className: "border-(--border-default) bg-(--bg-canvas) text-(--fg-muted)",
    },
    approved: {
      label: "Approved",
      className: "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)",
    },
    "change-requested": {
      label: "Change requested",
      className: "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)",
    },
    revising: {
      label: "Revising…",
      className: "border-(--border-default) bg-(--bg-canvas) text-(--fg-muted)",
    },
    updated: {
      label: "Updated",
      className: "border-(--fg-brand) bg-(--bg-sky) text-(--fg-brand)",
    },
  };

/**
 * Controlled metric card for the initial review and one-at-a-time revision
 * queue. Workflow state belongs to the intake flow; this component only
 * renders the supplied status and reports user actions through callbacks.
 */
export function MetricReviewCard({
  id,
  name,
  definition,
  unit,
  formula,
  status,
  isChangeRequestEditorOpen = false,
  changeRequestDraft = "",
  changeRequest,
  isProcessing = false,
  disabled = false,
  onApprove,
  onRequestChange,
  onChangeRequestDraft,
  onSaveChangeRequest,
  onCancelChangeRequest,
  onEditChangeRequest,
  onRemoveChangeRequest,
}: MetricReviewCardProps) {
  const statusMeta = STATUS[status];
  const isActive =
    status === "approved" ||
    status === "change-requested" ||
    status === "revising" ||
    status === "updated";
  const hasSavedChangeRequest = Boolean(changeRequest);
  const headingId = `metric-review-card-${id}`;

  return (
    <article
      aria-labelledby={headingId}
      className={[
        "relative overflow-hidden rounded-[12px] border bg-(--bg-white) p-[14px]",
        isActive ? "border-(--fg-brand)" : "border-(--border-default)",
      ].join(" ")}
    >
      <header className="flex items-center gap-2.5">
        <h3
          id={headingId}
          className="min-w-0 text-[13.5px] font-medium text-(--fg-primary)"
        >
          {name}
        </h3>
        {unit && (
          <span className="rounded-full bg-(--bg-canvas) px-2 py-0.5 text-[11px] font-medium text-(--fg-secondary)">
            {unit}
          </span>
        )}
        <span
          className={[
            "ml-auto shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium",
            statusMeta.className,
          ].join(" ")}
        >
          {statusMeta.label}
        </span>
      </header>

      {definition && (
        <div className="mt-1.5 text-[12.5px] leading-[1.5] text-(--fg-secondary)">
          {definition}
        </div>
      )}

      {formula && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-[8px] border border-(--border-subtle) border-l-[3px] border-l-(--border-strong) bg-(--bg-canvas) px-2.5 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-(--fg-muted)">
            Formula
          </span>
          {formula}
        </div>
      )}

      {isChangeRequestEditorOpen ? (
        <div className="mt-2.5">
          <textarea
            value={changeRequestDraft}
            onChange={(event) => onChangeRequestDraft?.(event.target.value)}
            disabled={disabled}
            rows={1}
            aria-label={`Requested change for ${name}`}
            placeholder="Describe the change you want to this metric…"
            className="block min-h-[34px] w-full resize-none rounded-[8px] border border-(--border-default) px-2.5 py-[7px] text-[13px] leading-[1.4] outline-none focus:border-(--fg-brand)"
          />
          <div className="mt-2 flex justify-end gap-1.5">
            {onCancelChangeRequest && (
              <button
                type="button"
                onClick={onCancelChangeRequest}
                disabled={disabled}
                className="cursor-pointer rounded-[8px] border border-(--border-default) bg-(--bg-white) px-2.5 py-1 text-[12px] font-medium text-(--fg-secondary) transition-colors hover:bg-(--bg-sky) disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            {onSaveChangeRequest && (
              <button
                type="button"
                onClick={() => onSaveChangeRequest(changeRequestDraft.trim())}
                disabled={disabled || changeRequestDraft.trim().length === 0}
                className="cursor-pointer rounded-[8px] border-0 bg-(--brand-navy) px-2.5 py-1 text-[12px] font-semibold text-white transition-colors hover:bg-(--steel-110) disabled:cursor-not-allowed disabled:opacity-50"
              >
                Save request
              </button>
            )}
          </div>
        </div>
      ) : hasSavedChangeRequest ? (
        <div className="mt-2.5 rounded-[8px] border border-(--fg-brand) bg-(--bg-sky) px-2.5 py-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-(--fg-brand)">
              Requested change
            </span>
            {(onEditChangeRequest || onRemoveChangeRequest) && (
              <div className="ml-auto flex gap-1.5">
                {onEditChangeRequest && (
                  <button
                    type="button"
                    onClick={onEditChangeRequest}
                    disabled={disabled}
                    className="cursor-pointer rounded-[6px] px-1.5 py-0.5 text-[12px] font-medium text-(--fg-brand) transition-colors hover:bg-(--bg-white) disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Edit
                  </button>
                )}
                {onRemoveChangeRequest && (
                  <button
                    type="button"
                    onClick={onRemoveChangeRequest}
                    disabled={disabled}
                    className="cursor-pointer rounded-[6px] px-1.5 py-0.5 text-[12px] font-medium text-(--fg-muted) transition-colors hover:bg-(--bg-white) disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="mt-1 text-[13px] leading-[1.45] text-(--fg-primary)">
            {changeRequest}
          </div>
        </div>
      ) : status === "needs-review" || status === "approved" ? (
        <div className="mt-2.5 flex justify-end gap-1.5">
          {onRequestChange && (
            <button
              type="button"
              onClick={onRequestChange}
              disabled={disabled}
              className="cursor-pointer rounded-[8px] border border-(--border-default) bg-(--bg-white) px-2.5 py-1 text-[12px] font-medium text-(--fg-secondary) transition-colors hover:bg-(--bg-sky) disabled:cursor-not-allowed disabled:opacity-50"
            >
              Request change
            </button>
          )}
          {onApprove && (
            <button
              type="button"
              onClick={onApprove}
              disabled={disabled}
              className={[
                "inline-flex cursor-pointer items-center gap-1 rounded-[8px] border px-2.5 py-1 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                status === "approved"
                  ? "border-(--brand-navy) bg-(--brand-navy) text-white"
                  : "border-(--border-default) bg-(--bg-white) text-(--fg-primary) hover:bg-(--bg-sky)",
              ].join(" ")}
            >
              <Check size={12} />{" "}
              {status === "approved" ? "Approved" : "Approve"}
            </button>
          )}
        </div>
      ) : null}

      {isProcessing && (
        <span
          aria-label={`Updating ${name}`}
          role="status"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,transparent,rgb(255_255_255_/_0.65),transparent)]"
        />
      )}
    </article>
  );
}
